import { ImgID } from "../../../image_loader/img_id.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { LeaderboardScreenPlayerInfo } from "./leaderboard_screen_player_info.js";
import type { LeaderboardScreenPlayerInfoConfig } from "./leaderboard_screen_player_info_config.js";

export interface LeaderboardRowBackdrop
{
  url: string;
  dispose(): void;
}

interface BackdropImage
{
  url: string;
  image: HTMLImageElement;
}

interface BackdropEntry
{
  key: string;
  refs: number;
  image: BackdropImage | null;
  ready: Promise<void>;
}

// The cache owns one reference; screens keep older sizes alive only while using them.
let cached: BackdropEntry | null = null;

function backdropDpr(): number
{
  return Math.max( 1, Math.min( 2, window.devicePixelRatio || 1 ) );
}

function backdropKey( width: number, height: number, dpr: number ): string
{
  return `${width},${height},${dpr}`;
}

function release( entry: BackdropEntry ): void
{
  if ( --entry.refs !== 0 || !entry.image ) return;
  URL.revokeObjectURL( entry.image.url );
  entry.image = null;
}

function backdropEntry( width: number, height: number ): BackdropEntry
{
  const dpr = backdropDpr();
  const key = backdropKey( width, height, dpr );
  if ( cached?.key === key ) return cached;

  const previous = cached;
  const entry: BackdropEntry = { key, refs: 1, image: null, ready: Promise.resolve() };
  cached = entry;
  if ( previous ) release( previous );
  entry.ready = renderBackdrop( width, height, dpr ).then( image =>
  {
    entry.image = image;
    // A prewarm may finish after a resize has already evicted its cache reference.
    if ( entry.refs === 0 )
    {
      URL.revokeObjectURL( image.url );
      entry.image = null;
    }
  }, error =>
  {
    if ( cached === entry )
    {
      cached = null;
      release( entry );
    }
    throw error;
  } );
  return entry;
}

function backdropHandle( entry: BackdropEntry ): LeaderboardRowBackdrop
{
  const url = entry.image!.url;
  let disposed = false;
  return {
    url,
    dispose()
    {
      if ( disposed ) return;
      disposed = true;
      release( entry );
    },
  };
}

/** Prepares and decodes the shared card before the screen's first visible rows. */
export async function prepareLeaderboardRowBackdrop( width: number, height: number ): Promise<void>
{
  await backdropEntry( width, height ).ready;
}

/** Acquires a prepared card synchronously, so the first row needs no placeholder. */
export function acquireLeaderboardRowBackdrop( width: number, height: number ): LeaderboardRowBackdrop | null
{
  const entry = cached;
  if ( !entry?.image || entry.key !== backdropKey( width, height, backdropDpr() ) ) return null;
  entry.refs++;
  return backdropHandle( entry );
}

export async function createLeaderboardRowBackdrop( width: number, height: number ): Promise<LeaderboardRowBackdrop>
{
  const entry = backdropEntry( width, height );
  // Keep this entry alive even if another size is prepared while generation is pending.
  entry.refs++;
  try
  {
    await entry.ready;
    return backdropHandle( entry );
  }
  catch ( error )
  {
    release( entry );
    throw error;
  }
}

/** One shared card image; rows only add their own avatar, flag and text over it. */
async function renderBackdrop( width: number, height: number, dpr: number ): Promise<BackdropImage>
{
  await Promise.all( [
    ImgID.UI_LeaderboardSubstratePlayerNew,
    ImgID.ProfileStarIcon,
    ImgID.UI_LeaderboardEye,
  ].map( id => ImagesHolder.ensure( id ) ) );

  const canvas = document.createElement( "canvas" );
  canvas.width = Math.ceil( width * dpr );
  canvas.height = Math.ceil( height * dpr );
  let row: LeaderboardScreenPlayerInfo | undefined;
  let blob: Blob;
  try
  {
    const ctx = canvas.getContext( "2d" );
    if ( !ctx )
      throw new Error( "Leaderboard row backdrop canvas is unavailable" );
    ctx.scale( dpr, dpr );
    const config: LeaderboardScreenPlayerInfoConfig = {
      is_player: false, name: "", level: 1, rating: 4, avatar_id: 0, facebook_av_url: null,
      score: 0, country_code: "", user_id: "", days_in_game: null,
    };
    row = new LeaderboardScreenPlayerInfo( ctx, 0, 0, width, height, config );
    row.hidePlayerParts( true );
    row.render();
    blob = await new Promise<Blob>( ( resolve, reject ) =>
    {
      canvas.toBlob( result => result
        ? resolve( result )
        : reject( new Error( "Leaderboard row backdrop encoding failed" ) ), "image/png" );
    } );
  }
  finally
  {
    row?.cleanup();
    canvas.width = 0;
    canvas.height = 0;
  }

  const url = URL.createObjectURL( blob );
  try
  {
    const image = new Image();
    image.src = url;
    await image.decode();
    return { url, image };
  }
  catch ( error )
  {
    URL.revokeObjectURL( url );
    throw error;
  }
}
